import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import PendingPoints from "./PendingPoints";
import PhoneField from "./PhoneField";
import Recognition from "./Recognition";
import { postLookup } from "./guestApi";

const LOOKUP_DEBOUNCE_MS = 450;

/**
 * "Your name for the order" — the ONE identity field in the whole flow.
 *
 * This is the check-in. There is no separate step: the guest types the number
 * they want their name called by, and that same number is what earns the points.
 * Asking twice is what the order-first architecture exists to remove.
 *
 * PhoneField is reused untouched because it carries the F-3 fix — it collapses a
 * dial code the guest typed on top of the one they selected. Re-implementing
 * entry here would silently reintroduce the 15-digit phantom customer.
 */
export default function OrderForm({
  venueCode,
  onChange,
  invalidPhone,
  invalidName,
  // A guest we already recognised — from the cached session or a bookmarked
  // card. When present the phone field never appears: re-asking someone we just
  // greeted by name is exactly the one-shot feel P4 exists to remove.
  knownGuest,
  onForgetGuest,
  cardHref,
}) {
  const { t } = useTranslation();

  const [phone, setPhone] = useState("");
  const [phoneValid, setPhoneValid] = useState(false);
  const [name, setName] = useState("");
  const [lookup, setLookup] = useState({ state: "idle" }); // idle|checking|known|new

  // Every lookup carries a sequence number and only the newest may land. Typing
  // a number emits several valid intermediate values (a 9-digit prefix of a
  // 10-digit number is still valid E.164), so responses genuinely do arrive out
  // of order, and a stale "unknown" overwriting a fresh "known" would ask a
  // returning guest to introduce themselves again.
  const seq = useRef(0);
  const lastQueried = useRef("");

  const handlePhone = useCallback((e164, valid) => {
    setPhone(e164);
    setPhoneValid(valid);
  }, []);

  useEffect(() => {
    if (!phoneValid || !phone) {
      seq.current += 1;               // cancel anything in flight
      lastQueried.current = "";
      setLookup({ state: "idle" });
      return undefined;
    }
    if (phone === lastQueried.current) return undefined;

    const mine = ++seq.current;
    setLookup({ state: "checking" });

    const timer = setTimeout(async () => {
      try {
        const res = await postLookup(venueCode, phone);
        if (mine !== seq.current) return;
        lastQueried.current = phone;
        if (res?.known) {
          setLookup({
            state: "known",
            name: res.customer?.name || "",
            balance: res.points?.balance ?? 0,
            pending: res.points?.pending ?? 0,
          });
          // Their stored name wins — they gave it to this venue already, and it
          // is what the counter will call out.
          if (res.customer?.name) setName(res.customer.name);
        } else {
          setLookup({ state: "new", pending: res?.points?.pending ?? 0 });
        }
      } catch (_) {
        if (mine !== seq.current) return;
        // A recognition failure must never block ordering — fall back to the
        // capture path and let the server resolve identity when the order lands.
        setLookup({ state: "new", pending: 0 });
      }
    }, LOOKUP_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [phone, phoneValid, venueCode]);

  const known = lookup.state === "known";

  // A cached/bookmarked guest is already identified: report them upward as
  // complete so the CTA enables with no phone typed at all.
  useEffect(() => {
    if (knownGuest) {
      onChange?.({
        phone: "",
        phoneValid: true,
        name: knownGuest.name || "",
        known: true,
        customerId: knownGuest.customerId || null,
        pending: knownGuest.pending ?? 0,
      });
      return;
    }
    onChange?.({
      phone,
      phoneValid,
      name: name.trim(),
      known,
      customerId: null,
      pending: lookup.pending ?? 0,
    });
  }, [knownGuest, phone, phoneValid, name, known, lookup.pending, onChange]);

  if (knownGuest) {
    return (
      <section className="ss-block">
        <div className="ss-capture">
          <div className="ss-snap known" aria-live="polite">
            <span className="ss-snap-ic" aria-hidden="true">✓</span>
            <div className="ss-snap-body">
              <div className="ss-snap-hi">{t("scan_stay.order.welcome_back", { name: knownGuest.name })}</div>
              <div className="ss-snap-sub">{t("scan_stay.order.points_will_add")}</div>
              {typeof knownGuest.balance === "number" && (
                <div className="ss-snap-bal">
                  <span className="ss-snap-bal-n">{knownGuest.balance}</span>
                  <span className="ss-snap-bal-l">{t("scan_stay.your_points")}</span>
                </div>
              )}
              <PendingPoints points={knownGuest.pending} />
            </div>
          </div>

          <div className="cc-known-actions">
            {cardHref && <a className="cc-link" href={cardHref}>{t("card.my_points")}</a>}
            <button type="button" className="cc-link muted" onClick={onForgetGuest}>
              {t("card.not_you")}
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="ss-block">
      <div className="ss-capture">
        <div className="ss-capture-head">
          <span className="ss-capture-ic" aria-hidden="true">📣</span>
          <div>
            <h2 className="ss-capture-title">{t("scan_stay.order.name_title")}</h2>
            <div className="ss-capture-sub">{t("scan_stay.order.name_sub")}</div>
          </div>
        </div>

        <PhoneField initialE164="" onChange={handlePhone} invalid={invalidPhone} />

        <Recognition
          state={lookup.state}
          name={lookup.name}
          balance={lookup.balance}
          pending={lookup.pending}
          currencyNote={t("scan_stay.order.points_will_add")}
        />

        {/* Only ask for a name we don't already have. A recognised guest sees
            theirs echoed back, not an empty box to retype. */}
        {known ? (
          <div className="ss-locked-name">
            <span className="ss-locked-lab">{t("scan_stay.order.calling_you")}</span>
            <span className="ss-locked-val">{name}</span>
          </div>
        ) : (
          <div className="ss-field">
            <label htmlFor="ss-callname">{t("scan_stay.order.your_name")}</label>
            <input
              id="ss-callname"
              type="text"
              autoComplete="given-name"
              className={invalidName ? "invalid" : undefined}
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 60))}
              placeholder={t("scan_stay.name_ph")}
            />
          </div>
        )}
      </div>
    </section>
  );
}
